/**
 * P1-010: Treatment Intent Service
 * 
 * Handles storing and consuming treatment intent across auth boundaries.
 * When a user selects a treatment while not logged in, the intent is stored.
 * After successful login/signup, the intent is consumed to create a lead.
 * 
 * Intent is stored in localStorage (survives page refresh).
 * Dedup is handled server-side (same patient + same treatment = skip).
 */

import api from '../utils/api';

const STORAGE_KEY = 'treatx_treatment_intent';

/**
 * Check if there's a pending treatment intent.
 * Returns the intent object or null.
 */
export function getPendingTreatmentIntent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw);
    // Expire after 24 hours
    if (intent.timestamp && Date.now() - intent.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return intent;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Submit pending treatment intent to backend.
 * Creates a lead and returns the result.
 * Clears intent on success OR dedup (idempotent).
 * 
 * @returns {Object|null} Result with { success, lead_id, message, deduplicated } or null if no intent
 */
export async function submitPendingTreatmentIntent() {
  const intent = getPendingTreatmentIntent();
  if (!intent) return null;

  try {
    const res = await api.post('/patient/treatment-request', {
      treatment_id: intent.treatment_id || '',
      treatment_name: intent.treatment_name || '',
      clinic_id: intent.clinic_id || '',
      message: intent.message || '',
    });

    // Clear intent on success (or dedup)
    localStorage.removeItem(STORAGE_KEY);
    // Also clear legacy sessionStorage
    sessionStorage.removeItem('preselectedTreatment');
    sessionStorage.removeItem('preselectedTreatmentId');
    sessionStorage.removeItem('redirectAfterLogin');

    return res.data;
  } catch (err) {
    const detail = err.response?.data?.detail || err.message;
    console.error('[TreatmentIntent] Failed to submit:', detail);
    // Don't clear on network error — retry on next page load
    // DO clear on validation errors (won't succeed on retry)
    if (err.response?.status === 400 || err.response?.status === 422) {
      localStorage.removeItem(STORAGE_KEY);
    }
    return { success: false, error: detail };
  }
}

/**
 * Clear any pending treatment intent (e.g. after manual dismissal).
 */
export function clearTreatmentIntent() {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem('preselectedTreatment');
  sessionStorage.removeItem('preselectedTreatmentId');
  sessionStorage.removeItem('redirectAfterLogin');
}
